import React, { useEffect, useRef } from "react";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  orderStatusOptions,
  yesOrNoOptions,
  isReadOnlyUser,
  formatDate,
} from "../../../shared/utility";
import SelectMenu from "../../controls/SelectMenu";
import OrderProductsTable from "./OrderProductsTable";
import OrderPluSummaryTable from "./OrderPluSummaryTable";
import ShipmentProductsTabs from "./ShipmentProductsTabs";
import Input from "../../controls/Input";
import { useForm } from "../useForm";
import ButtonDropdown from "../../controls/ButtonDropdown";
import TextArea from "../../controls/TextArea";
import { Link } from "react-router-dom";
import Pills, { PillType } from "../../controls/Pills";

const initialFValues = {
  id: 0,
  customer: null,
  customer_id: null,
  invoice_number: "",
  order_date: new Date(),
  expected_date: null,
  status: null,
  notes: "",
  memo: "",
  mixed_cartons: [],
  credit_returns: [],
};

const truckQuestions = [
  {
    name: "truck_question_1",
    title: "Truck is clean, no foul odour or condensation present",
  },
  {
    name: "truck_question_2",
    title:
      "Truck has no rust, mold, flaking paint. No damage to floors, walls or ceiling",
  },
  {
    name: "truck_question_3",
    title:
      "Truck doors are sealed properly to prevent entrance of pests, dust and foreign material during transport",
  },
  { name: "truck_question_4", title: "Product condition is acceptable" },
];

export default function CreateNewOrderForm(props) {
  const {
    carriers,
    customers,
    isEdit,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    onUpdateOrderItem,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    onSavePluPriceGroup,
    onSavePluTaxGroup,
    onUpdateSummaryDiscount,
    onSetProductPromotionBypass,
    onRefreshPromotions,
    promotionRefreshLocked,
    promotionBypassLocked,
    onUpdateSourceItemTax,
    onBulkUpdateSourceItemTaxIncluded,
    pricingLocked,
    taxRateOptions,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
    closed,
  } = props;

  const isReadOnly = isReadOnlyUser();

  const buttonOptions = [
    {
      name: "Print Invoice",
      onClick: printOrderInvoice,
      inProgress: false,
    },
    {
      name: "Print Shipping Summary",
      onClick: printShippingSummary,
      inProgress: false,
    },
    {
      name: "Print Shipping Slip",
      onClick: printShippingDetails,
      inProgress: false,
    },
  ];

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.customer = null;
    if (values.customer === "") {
      temp.customer = "Please fill out field.";
    }

    temp.expected_date = null;
    if (values.expected_date === "") {
      temp.expected_date = "Please fill out field.";
    }

    temp.invoice_number = null;
    if (values.invoice_number === "") {
      temp.invoice_number = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  const valuesRef = useRef(values);
  valuesRef.current = values;
  const dirtyHeaderFields = useRef(new Set());
  const currentOrderId = useRef(null);

  const handleHeaderInputChange = (event) => {
    dirtyHeaderFields.current.add(event.target.name);
    handleInputChange(event);
  };

  useEffect(() => {
    if (order) {
      var truckQuestion1 = [];
      if (order.truck_question_1) {
        truckQuestion1 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_1,
        );
      }

      var truckQuestion2 = [];
      if (order.truck_question_2) {
        truckQuestion2 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_2,
        );
      }

      var truckQuestion3 = [];
      if (order.truck_question_3) {
        truckQuestion3 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_3,
        );
      }

      var truckQuestion4 = [];
      if (order.truck_question_4) {
        truckQuestion4 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_4,
        );
      }

      const orderCopy = {
        ...order,
        status: orderStatusOptions.find(
          (option) => option.value === order.status,
        ),
        customer: customers.find(
          (option) => option.value === order.customer_id,
        ),
        carrier: carriers.find((option) => option.value === order.carrier_id),
        memo: order.memo ?? "",
        invoice_number: order.invoice_number ?? "",
        truck_question_1: truckQuestion1,
        truck_question_2: truckQuestion2,
        truck_question_3: truckQuestion3,
        truck_question_4: truckQuestion4,
      };

      const orderId = order.id == null ? null : String(order.id);
      const orderChanged = currentOrderId.current !== orderId;
      if (orderChanged) {
        dirtyHeaderFields.current.clear();
        currentOrderId.current = orderId;
      }

      const headerFields = [
        "customer",
        "carrier",
        "status",
        "po_number",
        "invoice_number",
        "memo",
        "notes",
        "truck_question_1",
        "truck_question_2",
        "truck_question_3",
        "truck_question_4",
      ];
      const valueForComparison = (value) => value?.value ?? value ?? "";
      const refreshedValues = { ...orderCopy };

      if (!orderChanged) {
        headerFields.forEach((field) => {
          if (!dirtyHeaderFields.current.has(field)) return;

          const localValue = valuesRef.current[field];
          if (
            valueForComparison(localValue) ===
            valueForComparison(orderCopy[field])
          ) {
            // The server now reflects this edit. Use the refreshed option object
            // and let future order refreshes update the field normally.
            dirtyHeaderFields.current.delete(field);
          } else {
            refreshedValues[field] = localValue;
          }
        });
      }

      setValues(refreshedValues);
    }
  }, [carriers, customers, order, setValues]);

  const handleSubmit = () => {
    if (validate()) {
      updateOrderApiCall(values);
    }
  };

  const onItemEditComplete = async function (newData) {
    if (isEdit) {
      return onUpdateOrderItem(newData);
    }
    return { success: false };
  };

  const inputClassName =
    "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm";

  const answeredTruckCount = truckQuestions.filter(
    (q) => values[q.name]?.value != null,
  ).length;

  return (
    <div>
      {order || !isEdit ? (
        <div className="flex flex-col gap-3">
          <div className="min-w-0 text-sm">
            <div className="card-custom p-2.5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <SelectMenu
                  handleInputChange={handleHeaderInputChange}
                  name="customer"
                  options={customers.filter((option) => option.value !== -1)}
                  value={isEdit ? values.customer : null}
                  title={"Customer"}
                  error={errors.customer}
                  showLabel={true}
                  disabled={closed}
                />

              
<Input
                  label="PO #"
                  name="po_number"
                  onChange={handleHeaderInputChange}
                  value={values.po_number}
                  labelOn={true}
                  type="text"
                  error={errors.po_number}
                  className={inputClassName}
                />

<SelectMenu
                  handleInputChange={handleHeaderInputChange}
                  name="carrier"
                  options={carriers.filter((option) => option.value !== -1)}
                  value={isEdit ? values.carrier : null}
                  title={"Carrier"}
                  error={errors.carrier}
                  showLabel={true}
                  disabled={closed}
                />

                 {isEdit ? (
                  <SelectMenu
                    handleInputChange={handleHeaderInputChange}
                    name="status"
                    options={orderStatusOptions.filter(
                      (option) => option.value !== -1,
                    )}
                    value={isEdit ? values.status : null}
                    title={"Status"}
                    error={errors.status}
                    showLabel={true}
                  />
                ) : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <Input
                  label="Invoice #"
                  name="invoice_number"
                  onChange={handleInputChange}
                  value={values.invoice_number}
                  labelOn={true}
                  type="text"
                  error={errors.invoice_number}
                  className={inputClassName}
                  disabled={true}
                />

<Input
                  label="Created By"
                  name="created_by"
                  onChange={handleInputChange}
                  value={values.created_by_name}
                  labelOn={true}
                  type="text"
                  error={errors.created_by_name}
                  className={inputClassName}
                  disabled={true}
                />

                <Input
                  label="Shipped Date"
                  name="created_at"
                  onChange={handleInputChange}
                  value={formatDate(values.created_at)}
                  labelOn={true}
                  type="text"
                  error={errors.created_at}
                  className={inputClassName}
                  disabled={true}
                /> 
              </div>

              <Disclosure
                as="div"
                defaultOpen={false}
                className="border border-gray-200 rounded-md"
              >
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md">
                      <span>Truck inspection</span>
                      <span className="flex items-center gap-2 text-gray-500 font-normal">
                        {!open && (
                          <span>{answeredTruckCount}/4 answered</span>
                        )}
                        {open ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </span>
                    </Disclosure.Button>

                    <Transition
                      enter="transition duration-200 ease-out"
                      enterFrom="opacity-0 -translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition duration-150 ease-in"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 -translate-y-1"
                    >
                      <Disclosure.Panel className="px-3 pb-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 pt-2">
                          {truckQuestions.map((q) => (
                            <SelectMenu
                              key={q.name}
                              name={q.name}
                              options={yesOrNoOptions}
                              value={values[q.name]}
                              handleInputChange={handleHeaderInputChange}
                              title={q.title}
                              error={errors[q.name]}
                              disabled={closed}
                            />
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  label="Memo"
                  labelOn={true}
                  name="memo"
                  onChange={handleHeaderInputChange}
                  value={values.memo}
                  rows="2"
                  disabled={closed}
                />

                <TextArea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  label="Notes"
                  labelOn={true}
                  name="notes"
                  onChange={handleHeaderInputChange}
                  value={values.notes ?? ""}
                  rows="2"
                  disabled={closed}
                />
              </div>

              {!isReadOnly && isEdit ? (
                <div className="flex justify-end pt-2">
                  <ButtonDropdown
                    mainOption="Save"
                    items={buttonOptions}
                    mainAction={handleSubmit}
                  />
                </div>
              ) : null}
            </div>
          </div>

          {isEdit && values.credit_returns?.length > 0 ? (
            <div className="card-custom p-2.5">
              <p className="text-sm font-semibold text-gray-900 mb-2">
                Credit Returns
              </p>
              <ul className="space-y-2">
                {values.credit_returns.map((cr) => (
                  <li
                    key={cr.id}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <Link
                      to={`/credit-returns/${cr.id}`}
                      className="font-semibold main-purple"
                    >
                      {cr.name}
                    </Link>
                    <Pills
                      content={
                        cr.status === 1
                          ? { text: "Closed", type: PillType.Success }
                          : { text: "Open", type: PillType.DarkYellow }
                      }
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="min-w-0">
            <div className="card-custom p-2">
              {isEdit ? (
                <ShipmentProductsTabs
                  summary={
                    <OrderPluSummaryTable
                      values={values}
                      closed={closed}
                      onSavePluPriceGroup={onSavePluPriceGroup}
                      onSavePluTaxGroup={onSavePluTaxGroup}
                      onUpdateSummaryDiscount={onUpdateSummaryDiscount}
                      onBulkUpdateSourceItemTaxIncluded={onBulkUpdateSourceItemTaxIncluded}
                      pricingLocked={pricingLocked}
                      onSetProductPromotionBypass={onSetProductPromotionBypass}
                      onRefreshPromotions={onRefreshPromotions}
                      promotionRefreshLocked={promotionRefreshLocked}
                      promotionBypassLocked={promotionBypassLocked}
                      taxRateOptions={taxRateOptions}
                      showErrorModal={showErrorModal}
                    />
                  }
                  details={
                    <OrderProductsTable
                      loading={false}
                      values={values}
                      onItemEditComplete={onItemEditComplete}
                      closed={closed}
                      setEmptyModelOpen={setEmptyModelOpen}
                      showErrorModal={showErrorModal}
                      onCreatePriceSplit={onCreatePriceSplit}
                      onUpdatePriceSplit={onUpdatePriceSplit}
                      onDeletePriceSplit={onDeletePriceSplit}
                      onUpdateSourceItemTax={onUpdateSourceItemTax}
                      onBulkUpdateSourceItemTaxIncluded={onBulkUpdateSourceItemTaxIncluded}
                      pricingLocked={pricingLocked}
                      taxRateOptions={taxRateOptions}
                    />
                  }
                />
              ) : (
                <OrderProductsTable
                  loading={false}
                  values={values}
                  onItemEditComplete={onItemEditComplete}
                  closed={closed}
                  setEmptyModelOpen={setEmptyModelOpen}
                  showErrorModal={showErrorModal}
                  onCreatePriceSplit={onCreatePriceSplit}
                  onUpdatePriceSplit={onUpdatePriceSplit}
                  onDeletePriceSplit={onDeletePriceSplit}
                  onUpdateSourceItemTax={onUpdateSourceItemTax}
                  onBulkUpdateSourceItemTaxIncluded={onBulkUpdateSourceItemTaxIncluded}
                  pricingLocked={pricingLocked}
                  taxRateOptions={taxRateOptions}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
